import { FC, useMemo, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Typography, TabsContent } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconRefresh } from "@tabler/icons-react";
import { Loader } from "../../components/Loader";
import {
  getTestCases,
  getCriterions,
  getPlaygroundAgents,
  getDatamates,
  getKnowledgeBaseSources,
} from "../../services/evals";
import { useLocalAgents } from "../hooks/useLocalAgents";
import { useRunEvaluation } from "../hooks/useRunEvaluation";
import { EvaluationProgressDialog } from "./EvaluationProgressDialog";
import { EvaluationContextSelector } from "./EvaluationContextSelector";
import { AgentSelector } from "./AgentSelector";
import { TestCaseSelector } from "./TestCaseSelector";
import { CriteriaSelector } from "./CriteriaSelector";
import { EvaluationSummary } from "./EvaluationSummary";
import { TagsFilter } from "./TagsFilter";

interface RunEvaluationTabProps {
  onEvaluate?: (data: { agentIds: string[]; testCaseIds: string[]; criteriaIds: string[] }) => void;
}

export const RunEvaluationTab: FC<RunEvaluationTabProps> = ({ onEvaluate }) => {
  const { localAgents } = useLocalAgents();

  // Fetch test cases
  const {
    data: testCases,
    isPending: testCasesLoading,
    refetch: refetchTestCases,
  } = useQuery({
    queryKey: ["testCases"],
    queryFn: getTestCases,
  });

  // Fetch criteria
  const {
    data: criteria,
    isPending: criteriaLoading,
    refetch: refetchCriteria,
  } = useQuery({
    queryKey: ["criteria"],
    queryFn: getCriterions,
  });

  // Fetch backend agents
  const {
    data: agentsData,
    isPending: agentsLoading,
    refetch: refetchAgents,
  } = useQuery({
    queryKey: ["playgroundAgents"],
    queryFn: getPlaygroundAgents,
  });

  // Fetch datamates
  const { data: datamatesData, isLoading: datamatesLoading } = useQuery({
    queryKey: ["datamates"],
    queryFn: getDatamates,
  });

  // Fetch knowledge bases
  const { data: knowledgeBasesData, isLoading: knowledgeBasesLoading } = useQuery({
    queryKey: ["knowledgeBases"],
    queryFn: getKnowledgeBaseSources,
  });

  // Use custom hook for evaluation logic
  const {
    selectedAgents,
    selectedTestCases,
    selectedCriteria,
    selectedDatamates,
    selectedKnowledgeBases,
    runName,
    setSelectedDatamates,
    setSelectedKnowledgeBases,
    setRunName,
    progressDialogOpen,
    currentJobId,
    setProgressDialogOpen,
    setCurrentJobId,
    evaluationMutation,
    handleToggleAgent,
    handleToggleTestCase,
    handleToggleCriterion,
    handleSelectAllAgents,
    handleDeselectAllAgents,
    handleSelectAllTestCases: hookHandleSelectAllTestCases,
    handleDeselectAllTestCases,
    handleSelectAllCriteria,
    handleDeselectAllCriteria,
    handleEvaluate,
  } = useRunEvaluation({
    localAgents,
    backendAgentsMap: agentsData?.agents || {},
    criteria,
    onEvaluate,
  });

  const handleRefresh = () => {
    refetchTestCases();
    refetchCriteria();
    refetchAgents();
  };

  const backendAgents = agentsData?.agents || {};
  const backendAgentsList = Object.entries(backendAgents).map(([name, details]) => ({
    id: name,
    name: details.name,
    description: details.description,
    isLocal: false,
  }));

  const allAgents = [...localAgents.map(a => ({ ...a, isLocal: true })), ...backendAgentsList];

  const testCasesList = useMemo(() => (Array.isArray(testCases) ? testCases : []), [testCases]);
  const criteriaList = Array.isArray(criteria) ? criteria : [];
  const datamatesList = Array.isArray(datamatesData?.datamates) ? datamatesData.datamates : [];
  const knowledgeBasesList = Array.isArray(knowledgeBasesData) ? knowledgeBasesData : [];

  // Tag filtering state
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const allTags = useMemo(
    () =>
      testCasesList.length > 0
        ? Array.from(new Set(testCasesList.flatMap(tc => tc.tags || []))).sort()
        : [],
    [testCasesList]
  );

  const filteredTestCases = useMemo(
    () =>
      selectedTags.length > 0
        ? testCasesList.filter(tc => selectedTags.some(tag => tc.tags?.includes(tag)))
        : testCasesList,
    [testCasesList, selectedTags]
  );

  const handleToggleTag = useCallback((tag: string) => {
    setSelectedTags(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]));
  }, []);

  const handleSelectAllTestCases = () => hookHandleSelectAllTestCases(filteredTestCases);

  const isLoading =
    testCasesLoading ||
    criteriaLoading ||
    agentsLoading ||
    datamatesLoading ||
    knowledgeBasesLoading;

  // Each test case is run once with ALL selected agents
  const totalEvaluations = selectedTestCases.length;

  return (
    <TabsContent value="run-evaluation" className="al-space-y-4">
      <Stack direction="row" justify="between" align="center">
        <div>
          <Typography size="lg" weight="medium">
            Run Evaluation
          </Typography>
          <Typography size="sm" className="al-text-gray-500 al-mt-1">
            Select agents, test cases, and criteria to evaluate
          </Typography>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <IconRefresh className="al-w-4 al-h-4 al-mr-2" />
          Refresh
        </Button>
      </Stack>

      {isLoading ? (
        <Loader label="Loading..." />
      ) : (
        <>
          <TagsFilter
            allTags={allTags}
            selectedTags={selectedTags}
            testCases={testCasesList}
            onToggleTag={handleToggleTag}
            setSelectedTags={setSelectedTags}
            onSelectAll={() => setSelectedTags([...allTags])}
            onDeselectAll={() => setSelectedTags([])}
          />

          <div className="al-grid al-grid-cols-3 al-gap-4">
            <AgentSelector
              agents={allAgents}
              selectedAgents={selectedAgents}
              onToggle={handleToggleAgent}
              onSelectAll={handleSelectAllAgents}
              onDeselectAll={handleDeselectAllAgents}
            />

            <TestCaseSelector
              testCases={filteredTestCases}
              selectedTestCases={selectedTestCases}
              onToggle={handleToggleTestCase}
              onSelectAll={handleSelectAllTestCases}
              onDeselectAll={handleDeselectAllTestCases}
            />

            <CriteriaSelector
              criteria={criteriaList}
              selectedCriteria={selectedCriteria}
              onToggle={handleToggleCriterion}
              onSelectAll={handleSelectAllCriteria}
              onDeselectAll={handleDeselectAllCriteria}
            />
          </div>

          {/* Global Context Selection */}
          <EvaluationContextSelector
            datamates={datamatesList}
            knowledgeBases={knowledgeBasesList}
            selectedDatamates={selectedDatamates}
            selectedKnowledgeBases={selectedKnowledgeBases}
            onDatamatesChange={setSelectedDatamates}
            onKnowledgeBasesChange={setSelectedKnowledgeBases}
          />
        </>
      )}

      {/* Evaluation Summary and Run Button */}
      <EvaluationSummary
        selectedTestCasesCount={selectedTestCases.length}
        selectedAgentsCount={selectedAgents.length}
        selectedCriteriaCount={selectedCriteria.length}
        totalEvaluations={totalEvaluations}
        runName={runName}
        setRunName={setRunName}
        isLoading={evaluationMutation.isPending}
        onEvaluate={handleEvaluate}
      />

      {/* Progress Dialog */}
      <EvaluationProgressDialog
        jobId={currentJobId}
        open={progressDialogOpen}
        onClose={() => {
          setProgressDialogOpen(false);
          setCurrentJobId(null);
        }}
      />
    </TabsContent>
  );
};
